import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { Smartphone, Zap, Shield, Clock, Users, CheckCircle, ArrowRight, Mic, Settings, Heart, Volume2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link as RouterLink } from 'react-router-dom';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { FloatingIslandCard } from '@/components/cards/FloatingIslandCard';
import { HolographicCard } from '@/components/cards/HolographicCard';

const AIEnabledBedsideDevices = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);
  
  const alexaFeatures = [
    {
      icon: Zap,
      title: "Rapid Deployment",
      description: "Launch voice-enabled patient services in months, not years, leveraging proven healthcare-specific Alexa implementations.",
      number: "01"
    },
    {
      icon: Heart,
      title: "Focus on Patient Care",
      description: "Deliver exceptional voice experiences to your patients while we handle the technical complexity through pre-tested solutions.",
      number: "02"
    },
    {
      icon: Shield,
      title: "Cost-Effective Integration",
      description: "Deploy comprehensive voice-enabled patient services without investing in specialized development teams or extensive infrastructure.",
      number: "03"
    },
    {
      icon: Mic,
      title: "Healthcare-Specific Voice Commands",
      description: "Pre-configured voice interactions designed specifically for healthcare environments and patient needs.",
      number: "04"
    },
    {
      icon: Settings,
      title: "Strategic Implementation Guidance",
      description: "Expert support through our experienced provider network ensures smooth deployment and optimization.",
      number: "05"
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Faster Time to Market",
      description: "Deploy voice-enabled patient services 75% faster than traditional development approaches with pre-built healthcare solutions."
    },
    {
      icon: Users,
      title: "Enhanced Patient Experience",
      description: "Improve patient satisfaction scores through intuitive voice interactions for room controls, information requests, and care coordination."
    },
    {
      icon: Shield,
      title: "HIPAA-Compliant Voice Services",
      description: "Enterprise-grade security and privacy controls ensure all voice interactions meet healthcare compliance requirements."
    }
  ];

  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < alexaFeatures.length - 1) {
      setTimeout(() => setTypingCardIndex(index + 1), 500);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => setTypingCardIndex(0), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>AI-Enabled Bedside Devices - Alexa Smart Properties for Healthcare | Canary</title>
        <meta name="description" content="Rapid deployment for your organization. Launch voice-enabled patient services in months, not years, leveraging proven healthcare-specific Alexa implementations." />
      </Helmet>

      <div className="min-h-screen" style={{ background: 'var(--gradient-depth)' }}>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <NeuralBackground />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8">
                <Smartphone className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">AI-Enabled Bedside Devices</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                <SynapticText text="Alexa Smart Properties" delay={100} />
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                  <SynapticText text="for Healthcare" delay={800} />
                </span>
              </h1>
              
              <motion.p 
                className="text-xl text-primary-foreground mb-8 max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
              >
                Rapid deployment for your organization. Launch voice-enabled patient services in months, not years, 
                leveraging proven healthcare-specific Alexa implementations and our experienced provider network.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8 }}
              >
                <RouterLink 
                  to="/contact-request-demo"
                  className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-lg font-semibold
                           hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 transform hover:-translate-y-1
                           flex items-center justify-center gap-2"
                >
                  Request Voice Demo
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </RouterLink>
                
                <RouterLink 
                  to="/resources/case-studies"
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-lg font-semibold
                           hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  View Case Studies
                </RouterLink>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-24" style={{ background: 'var(--gradient-warm)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Three Core Advantages
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Transform your patient experience with voice-enabled bedside technology that puts care first.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-center p-8 rounded-xl bg-gradient-to-b from-primary/5 to-accent/5 border border-primary/10"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Rapid deployment for your organization</h3>
                <p className="text-white/70">
                  Launch voice-enabled patient services in months, not years, leveraging proven healthcare-specific 
                  Alexa implementations and our experienced provider network.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center p-8 rounded-xl bg-gradient-to-b from-primary/5 to-accent/5 border border-primary/10"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Focus on patient care, not technology</h3>
                <p className="text-white/70">
                  Deliver exceptional voice experiences to your patients while we handle the technical complexity 
                  through pre-tested healthcare solutions and strategic implementation guidance.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-center p-8 rounded-xl bg-gradient-to-b from-primary/5 to-accent/5 border border-primary/10"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Cost-effective voice integration</h3>
                <p className="text-white/70">
                  Deploy comprehensive voice-enabled patient services without investing in specialized development 
                  teams or extensive technical infrastructure.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Comprehensive Voice Solutions Accordion */}
        <section className="py-24" style={{ background: 'var(--gradient-earth)' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Comprehensive Voice-Enabled Healthcare Solutions
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Our platform provides everything you need to implement voice technology 
                that enhances patient care and operational efficiency.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-r from-primary/5 to-accent/5 backdrop-blur-sm border border-primary/10 rounded-2xl p-8 shadow-2xl"
            >
              <Accordion type="single" collapsible className="space-y-4">
                {alexaFeatures.map((feature, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="bg-background/50 backdrop-blur-sm border border-primary/20 rounded-xl overflow-hidden hover:border-primary/40 transition-all duration-300"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline group">
                      <div className="flex items-center gap-4 w-full">
                        <div className="w-12 h-12 bg-gradient-to-r from-primary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                          <feature.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-primary/70 bg-primary/10 px-2 py-1 rounded">
                              {feature.number}
                            </span>
                            <h3 className="text-lg font-semibold text-white group-hover:text-primary transition-colors">
                              {feature.title}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="ml-16 pt-2">
                        <p className="text-white/70 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24" style={{ background: 'var(--gradient-earth)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Proven Results in Healthcare Voice Technology
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Healthcare organizations using our voice-enabled bedside devices report 
                significant improvements in patient satisfaction and operational efficiency.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <FloatingIslandCard
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 border-t border-border/50" style={{ background: 'var(--gradient-warm)' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Transform Patient Care with Voice Technology?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Join leading healthcare organizations who have enhanced their patient experience 
                with our AI-enabled bedside devices and Alexa Smart Properties.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <RouterLink 
                  to="/contact-request-demo"
                  className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-lg font-semibold
                           hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 transform hover:-translate-y-1
                           flex items-center justify-center gap-2"
                >
                  Schedule Voice Technology Demo
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </RouterLink>
                
                <RouterLink 
                  to="/resources/white-papers-research"
                  className="group border-2 border-primary text-primary px-8 py-4 rounded-lg font-semibold
                           hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Download Implementation Guide
                </RouterLink>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AIEnabledBedsideDevices;